import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { Task, TaskList, TaskPatch, TasksResponse } from "@websede/contracts";
import {
  AlignLeft,
  ArrowDown,
  ArrowUp,
  Bell,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  GripVertical,
  List,
  ListChecks,
  Mail,
  Plus,
  Search,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.js";
import { ErrorNotice } from "../components/form.js";
import { ShellSearch, ShellSidebar, useShell } from "../components/shell-slots.js";

const ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const SORT_KEY = "ws_tasks_sort";
/** The virtual list of starred tasks across every list. */
const STARRED = "starred";
type SortMode = "manual" | "due";

/** Reminder choices, in minutes before due; date-only tasks count from midnight of the due day. */
const TIMED_REMINDERS = [
  { minutes: 0, key: "tasks.reminderAtDue" },
  { minutes: 15, key: "tasks.reminder15" },
  { minutes: 60, key: "tasks.reminder60" },
  { minutes: 1440, key: "tasks.reminder1440" },
] as const;
const DATE_REMINDERS = [
  { minutes: -540, key: "tasks.reminderDay9" },
  { minutes: 900, key: "tasks.reminderDayBefore9" },
] as const;

function readSortMode(): SortMode {
  try {
    return localStorage.getItem(SORT_KEY) === "due" ? "due" : "manual";
  } catch {
    return "manual";
  }
}

/** Google Tasks' layout: lists on the left, the open list in the middle, details at the side. */
export function TasksPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { closeDrawer } = useShell();
  const search = useSearch({ from: "/app/tasks" });
  const [showCompleted, setShowCompleted] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newList, setNewList] = useState("");
  const [creatingList, setCreatingList] = useState(false);
  const [query, setQuery] = useState("");
  const [sortMode, setSortModeState] = useState<SortMode>(readSortMode);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const setSortMode = (mode: SortMode) => {
    setSortModeState(mode);
    try {
      localStorage.setItem(SORT_KEY, mode);
    } catch {
      // The choice then lasts for this visit only.
    }
  };

  const lists = useQuery({ queryKey: ["tasks", "lists"], queryFn: () => api.tasks.lists() });
  const tasks = useQuery({ queryKey: ["tasks", "all"], queryFn: () => api.tasks.list() });
  const allTasks = tasks.data?.tasks ?? [];
  const selected = allTasks.find((task) => task.id === search.task) ?? null;
  const defaultListId =
    lists.data?.lists.find((l) => l.isDefault)?.id ?? lists.data?.lists[0]?.id ?? "";
  const listId = search.list ?? selected?.listId ?? defaultListId;
  const starredView = listId === STARRED;
  const currentList = lists.data?.lists.find((l) => l.id === listId);
  /** Where new tasks go: the open list, or the default list from the starred view. */
  const targetListId = starredView ? defaultListId : listId;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["tasks"] });
  const create = useMutation({
    mutationFn: (title: string) =>
      api.tasks.create({
        listId: targetListId,
        title,
        timeZone: ZONE,
        ...(starredView ? { important: true } : {}),
      }),
    onSuccess: () => {
      setNewTitle("");
      void refresh();
    },
  });
  const createList = useMutation({
    mutationFn: (name: string) => api.tasks.createList(name),
    onSuccess: (list) => {
      setNewList("");
      setCreatingList(false);
      closeDrawer();
      void refresh();
      void navigate({ to: "/tasks", search: { list: list.id } });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TaskPatch }) => api.tasks.update(id, patch),
    onSuccess: () => void refresh(),
  });
  // The new order shows at once; the server copy follows.
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.tasks.reorder(ids),
    onMutate: (ids) => {
      queryClient.setQueryData<TasksResponse>(["tasks", "all"], (data) =>
        data
          ? {
              tasks: data.tasks.map((task) => {
                const index = ids.indexOf(task.id);
                return index >= 0 ? { ...task, position: index } : task;
              }),
            }
          : data,
      );
    },
    onSettled: () => void refresh(),
  });

  const needle = query.trim().toLocaleLowerCase();
  const inList = useMemo(
    () =>
      allTasks.filter(
        (task) =>
          (starredView ? task.important : task.listId === listId) &&
          (!needle ||
            task.title.toLocaleLowerCase().includes(needle) ||
            (task.notes ?? "").toLocaleLowerCase().includes(needle)),
      ),
    [allTasks, listId, starredView, needle],
  );
  const topLevel = starredView ? inList : inList.filter((task) => !task.parentId);
  const open = sortTasks(
    topLevel.filter((task) => !task.completed),
    starredView ? "due" : sortMode,
  );
  const openIds = open.map((task) => task.id);
  const canDrag = sortMode === "manual" && !starredView && !needle;
  const moveTo = (id: string, targetIndex: number) => {
    const ids = openIds.filter((x) => x !== id);
    const index = Math.max(0, Math.min(targetIndex, ids.length));
    ids.splice(index, 0, id);
    if (ids.join() !== openIds.join()) reorder.mutate(ids);
  };
  const dropOn = (targetId: string) => {
    if (dragId && dragId !== targetId) moveTo(dragId, openIds.indexOf(targetId));
    setDragId(null);
    setOverId(null);
  };
  const done = topLevel.filter((task) => task.completed);
  const subtasksOf = (task: Task) =>
    starredView ? [] : allTasks.filter((x) => x.parentId === task.id);

  const select = (task: Task | null) =>
    void navigate({
      to: "/tasks",
      search: task
        ? { list: starredView ? STARRED : task.listId, task: task.id }
        : { list: listId },
    });
  const openList = (id: string) => {
    closeDrawer();
    void navigate({ to: "/tasks", search: { list: id } });
  };
  const startAdding = () => {
    closeDrawer();
    setAdding(true);
  };

  const onAdd = (event: FormEvent) => {
    event.preventDefault();
    if (newTitle.trim() && targetListId) create.mutate(newTitle.trim());
  };
  const openCount = (id: string) =>
    allTasks.filter((x) => x.listId === id && !x.completed && !x.parentId).length;
  const starredCount = allTasks.filter((x) => x.important && !x.completed).length;
  const heading = starredView
    ? t("tasks.starred")
    : currentList
      ? listName(currentList, t)
      : t("nav.tasks");

  const listButton = (id: string, label: string, count: number, Icon: typeof List) => {
    const active = id === listId;
    return (
      <button
        key={id}
        type="button"
        onClick={() => openList(id)}
        className={`flex h-9 items-center gap-4 rounded-r-full pr-3 pl-6 text-left text-sm ${active ? "bg-accent-soft font-medium text-accent" : "text-ink-2 hover:bg-ink/5"}`}
      >
        <Icon className="size-[18px] shrink-0" strokeWidth={active ? 2.2 : 1.8} />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {count > 0 ? <span className="text-xs tabular-nums">{count}</span> : null}
      </button>
    );
  };

  return (
    <div className="relative flex h-full min-h-0">
      <ShellSearch>
        <div className="flex h-full min-w-0 flex-1 items-center">
          <Search className="ml-4 hidden size-5 shrink-0 text-ink-2 md:block" />
          <input
            type="search"
            className="h-full min-w-0 flex-1 bg-transparent px-2 text-base outline-none placeholder:text-ink-2 md:px-3"
            placeholder={t("tasks.search")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query ? (
            <button
              type="button"
              aria-label={t("mail.clearSearch")}
              className="flex size-10 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
              onClick={() => setQuery("")}
            >
              <X className="size-5" />
            </button>
          ) : null}
        </div>
      </ShellSearch>

      <ShellSidebar>
        <div className="hidden px-2 pt-1 pb-4 md:block">
          <button
            type="button"
            onClick={startAdding}
            className="flex h-14 items-center gap-3 rounded-2xl bg-accent-soft pr-6 pl-4 text-sm font-medium text-accent hover:shadow-1"
          >
            <Plus className="size-5" />
            {t("tasks.addTask")}
          </button>
        </div>
        <nav aria-label={t("nav.tasks")} className="flex flex-col pr-2 md:pr-3">
          {listButton(STARRED, t("tasks.starred"), starredCount, Star)}
          <div className="mt-3 mb-1 pl-6 text-xs font-medium tracking-wide text-ink-3 uppercase">
            {t("tasks.lists")}
          </div>
          {(lists.data?.lists ?? []).map((list) =>
            listButton(list.id, listName(list, t), openCount(list.id), List),
          )}
          {creatingList ? (
            <form
              className="py-1 pr-2 pl-6"
              onSubmit={(event) => {
                event.preventDefault();
                if (newList.trim()) createList.mutate(newList.trim());
              }}
            >
              <input
                // biome-ignore lint/a11y/noAutofocus: opened on purpose to type the name
                autoFocus
                className="h-9 w-full rounded-lg border border-accent bg-surface px-3 text-sm outline-none"
                placeholder={t("tasks.newListName")}
                value={newList}
                onChange={(e) => setNewList(e.target.value)}
                onBlur={() => {
                  if (!newList.trim()) setCreatingList(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setCreatingList(false);
                }}
              />
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setCreatingList(true)}
              className="flex h-9 items-center gap-4 rounded-r-full pr-3 pl-6 text-left text-sm text-ink-2 hover:bg-ink/5"
            >
              <Plus className="size-[18px]" />
              {t("tasks.newList")}
            </button>
          )}
        </nav>
      </ShellSidebar>

      <section
        className={`${selected ? "hidden md:flex" : "flex"} min-w-0 flex-1 flex-col overflow-auto pb-24 md:pb-6`}
      >
        <div className="mx-auto flex w-full max-w-3xl flex-col px-2 md:px-6">
          <header className="flex items-center gap-2 px-2 pt-4 pb-2">
            <h1 className="min-w-0 flex-1 truncate text-[22px] text-ink">{heading}</h1>
            {starredView ? null : (
              <select
                aria-label={t("tasks.sortBy")}
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value as SortMode)}
                className="h-9 rounded-full border border-rule bg-surface px-3 text-sm text-ink-2 outline-none hover:bg-surface-2"
              >
                <option value="manual">{t("tasks.sortManual")}</option>
                <option value="due">{t("tasks.sortDue")}</option>
              </select>
            )}
          </header>

          {adding ? (
            <form onSubmit={onAdd} className="flex items-center gap-3 px-2 py-2">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full border-2 border-ink-3/60" />
              <input
                // biome-ignore lint/a11y/noAutofocus: the user asked to add a task
                autoFocus
                className="h-10 min-w-0 flex-1 border-b border-accent bg-transparent text-sm outline-none"
                placeholder={t("tasks.addPlaceholder")}
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onBlur={() => {
                  if (!newTitle.trim()) setAdding(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setAdding(false);
                }}
                disabled={!targetListId}
              />
            </form>
          ) : (
            <button
              type="button"
              onClick={startAdding}
              className="flex h-11 items-center gap-3 rounded-lg px-2 text-sm font-medium text-accent hover:bg-accent-soft/60"
            >
              <span className="flex size-6 items-center justify-center">
                <Plus className="size-5" />
              </span>
              {t("tasks.addTask")}
            </button>
          )}

          <ErrorNotice error={tasks.error ?? create.error ?? update.error} />
          <ul className="flex flex-col">
            {open.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                subtasks={subtasksOf(task)}
                listLabel={
                  starredView
                    ? listName(
                        lists.data?.lists.find((l) => l.id === task.listId) ?? {
                          id: "",
                          name: "",
                          isDefault: false,
                        },
                        t,
                      )
                    : null
                }
                active={task.id === selected?.id}
                onToggle={(target) =>
                  update.mutate({ id: target.id, patch: { completed: !target.completed } })
                }
                onStar={() => update.mutate({ id: task.id, patch: { important: !task.important } })}
                onOpen={select}
                drag={
                  canDrag
                    ? {
                        isOver: overId === task.id && dragId !== task.id,
                        onStart: () => setDragId(task.id),
                        onEnter: () => setOverId(task.id),
                        onDrop: () => dropOn(task.id),
                        onEnd: () => {
                          setDragId(null);
                          setOverId(null);
                        },
                      }
                    : null
                }
              />
            ))}
          </ul>
          {tasks.data && open.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-14 text-center text-sm text-ink-3">
              <ListChecks className="size-12" strokeWidth={1.3} />
              {needle ? t("tasks.noMatch") : starredView ? t("tasks.noStarred") : t("tasks.empty")}
            </div>
          ) : null}
          {done.length > 0 ? (
            <div className="mt-2 border-t border-rule">
              <button
                type="button"
                className="flex h-11 w-full items-center gap-2 px-2 text-left text-sm font-medium text-ink-2 hover:text-ink"
                onClick={() => setShowCompleted((v) => !v)}
              >
                {showCompleted ? (
                  <ChevronDown className="size-4" />
                ) : (
                  <ChevronRight className="size-4" />
                )}
                {t("tasks.completed", { count: done.length })}
              </button>
              {showCompleted ? (
                <ul className="flex flex-col">
                  {done.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      subtasks={subtasksOf(task)}
                      listLabel={null}
                      active={task.id === selected?.id}
                      onToggle={(target) =>
                        update.mutate({ id: target.id, patch: { completed: !target.completed } })
                      }
                      onStar={() =>
                        update.mutate({ id: task.id, patch: { important: !task.important } })
                      }
                      onOpen={select}
                    />
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>

      {selected ? null : (
        <button
          type="button"
          aria-label={t("tasks.addTask")}
          onClick={startAdding}
          className="absolute right-4 bottom-4 flex size-14 items-center justify-center rounded-2xl bg-accent-soft text-accent shadow-2 md:hidden"
        >
          <Plus className="size-6" />
        </button>
      )}

      {selected ? (
        <TaskDetail
          key={selected.id}
          task={selected}
          lists={lists.data?.lists ?? []}
          subtasks={allTasks
            .filter((x) => x.parentId === selected.id)
            .sort((a, b) => (b.position ?? -Infinity) - (a.position ?? -Infinity))}
          onClose={() => select(null)}
          onChanged={() => void refresh()}
          move={
            canDrag && !selected.parentId && !selected.completed
              ? {
                  canUp: openIds.indexOf(selected.id) > 0,
                  canDown: openIds.indexOf(selected.id) < openIds.length - 1,
                  up: () => moveTo(selected.id, openIds.indexOf(selected.id) - 1),
                  down: () => moveTo(selected.id, openIds.indexOf(selected.id) + 1),
                }
              : null
          }
        />
      ) : null}
    </div>
  );
}

interface DragHandlers {
  isOver: boolean;
  onStart: () => void;
  onEnter: () => void;
  onDrop: () => void;
  onEnd: () => void;
}

/** Google's round checkbox: an empty circle that fills with a tick. */
function RoundCheck({
  checked,
  label,
  onToggle,
  small = false,
}: {
  checked: boolean;
  label: string;
  onToggle: () => void;
  small?: boolean;
}) {
  return (
    <label
      className={`group/check relative flex shrink-0 cursor-pointer items-center justify-center rounded-full border-2 ${small ? "size-5" : "size-6"} ${checked ? "border-accent bg-accent text-white" : "border-ink-3/70 text-accent hover:border-accent"}`}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        aria-label={label}
        onChange={onToggle}
      />
      <Check
        className={`${small ? "size-3" : "size-3.5"} ${checked ? "" : "opacity-0 group-hover/check:opacity-100"}`}
        strokeWidth={3}
      />
    </label>
  );
}

function DueChip({ task }: { task: Task }) {
  const { i18n } = useTranslation();
  if (!task.due) return null;
  const overdue = !task.completed && isOverdue(task);
  const soon = !overdue && relativeDays(task) <= 1;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${overdue ? "border-danger/40 bg-danger-soft text-danger" : soon ? "border-accent/40 bg-accent-soft text-accent" : "border-rule text-ink-2"}`}
    >
      <CalendarDays className="size-3" />
      {dueLabel(task, i18n.language)}
    </span>
  );
}

function TaskRow({
  task,
  subtasks,
  listLabel,
  active,
  onToggle,
  onStar,
  onOpen,
  drag = null,
}: {
  task: Task;
  subtasks: Task[];
  /** Shown in the starred view, where tasks come from several lists. */
  listLabel: string | null;
  active: boolean;
  onToggle: (task: Task) => void;
  onStar: () => void;
  onOpen: (task: Task) => void;
  /** Present in "my order": the row can be dragged onto another to take its place. */
  drag?: DragHandlers | null;
}) {
  const { t } = useTranslation();
  const openSubtasks = subtasks.filter((s) => !s.completed);
  return (
    <li
      className={`group rounded-lg ${drag?.isOver ? "shadow-[inset_0_2px_0_var(--color-accent)]" : ""}`}
      draggable={Boolean(drag)}
      onDragStart={(event) => {
        if (!drag) return;
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", task.id);
        drag.onStart();
      }}
      onDragOver={(event) => {
        if (!drag) return;
        event.preventDefault();
        drag.onEnter();
      }}
      onDrop={(event) => {
        if (!drag) return;
        event.preventDefault();
        drag.onDrop();
      }}
      onDragEnd={() => drag?.onEnd()}
    >
      <div
        className={`relative flex items-start gap-3 rounded-lg px-2 py-2.5 ${active ? "bg-accent-soft/70" : "hover:bg-ink/[0.04]"}`}
      >
        {drag ? (
          <GripVertical
            className="absolute top-3.5 -left-3 hidden size-4 cursor-grab text-ink-3 opacity-0 group-hover:opacity-100 md:block"
            aria-hidden="true"
          />
        ) : null}
        <span className="pt-0.5">
          <RoundCheck
            checked={task.completed}
            label={task.completed ? t("tasks.markOpen") : t("tasks.markDone")}
            onToggle={() => onToggle(task)}
          />
        </span>
        <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onOpen(task)}>
          <span
            className={`block text-sm leading-6 ${task.completed ? "text-ink-3 line-through" : "text-ink"}`}
          >
            {task.title}
          </span>
          {task.notes ? (
            <span className="block truncate text-xs text-ink-3">{task.notes}</span>
          ) : null}
          <span className="mt-1 flex flex-wrap items-center gap-1.5 empty:hidden">
            <DueChip task={task} />
            {task.due && task.reminderMinutes !== null ? (
              <Bell className="size-3.5 text-ink-3" aria-label={t("tasks.reminder")} />
            ) : null}
            {subtasks.length > 0 ? (
              <span className="inline-flex items-center gap-1 text-xs text-ink-3">
                <ListChecks className="size-3.5" />
                {subtasks.length - openSubtasks.length}/{subtasks.length}
              </span>
            ) : null}
            {task.email ? (
              <span className="inline-flex max-w-56 items-center gap-1 truncate rounded-full border border-rule px-2 py-0.5 text-xs text-ink-2">
                <Mail className="size-3 shrink-0" />
                <span className="truncate">{task.email.subject || t("tasks.fromEmail")}</span>
              </span>
            ) : null}
            {listLabel ? <span className="text-xs text-ink-3">{listLabel}</span> : null}
          </span>
        </button>
        <button
          type="button"
          aria-label={t("tasks.important")}
          aria-pressed={task.important}
          onClick={onStar}
          className={`flex size-8 shrink-0 items-center justify-center rounded-full hover:bg-ink/5 ${task.important ? "" : "opacity-60 md:opacity-0 md:group-hover:opacity-100"}`}
        >
          <Star
            className={`size-[18px] ${task.important ? "fill-[#f4b400] text-[#f4b400]" : "text-ink-3"}`}
          />
        </button>
      </div>
      {openSubtasks.length > 0 && !task.completed ? (
        <ul className="ml-9 flex flex-col border-l border-rule pl-2">
          {openSubtasks.map((sub) => (
            <li
              key={sub.id}
              className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-ink/[0.04]"
            >
              <RoundCheck
                small
                checked={sub.completed}
                label={t("tasks.markDone")}
                onToggle={() => onToggle(sub)}
              />
              <button
                type="button"
                className="min-w-0 flex-1 truncate text-left text-sm text-ink"
                onClick={() => onOpen(task)}
              >
                {sub.title}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function TaskDetail({
  task,
  lists,
  subtasks,
  onClose,
  onChanged,
  move,
}: {
  task: Task;
  lists: TaskList[];
  subtasks: Task[];
  onClose: () => void;
  onChanged: () => void;
  /** Up and down buttons for "my order", which is how phones reorder without dragging. */
  move: { canUp: boolean; canDown: boolean; up: () => void; down: () => void } | null;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(task.title);
  const [notes, setNotes] = useState(task.notes ?? "");
  const [subtask, setSubtask] = useState("");
  const notesRef = useRef<HTMLTextAreaElement>(null);
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TaskPatch }) => api.tasks.update(id, patch),
    onSuccess: onChanged,
  });
  const addSubtask = useMutation({
    mutationFn: (value: string) =>
      api.tasks.create({ listId: task.listId, title: value, timeZone: ZONE, parentId: task.id }),
    onSuccess: () => {
      setSubtask("");
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.tasks.remove(task.id),
    onSuccess: () => {
      onClose();
      onChanged();
    },
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const patch = (value: TaskPatch) => update.mutate({ id: task.id, patch: value });
  const date = task.due?.slice(0, 10) ?? "";
  const time = task.due && task.dueHasTime ? task.due.slice(11, 16) : "";
  const field =
    "h-10 rounded-lg bg-surface-2 px-3 text-sm outline-none focus:ring-2 focus:ring-accent/40 disabled:opacity-50";
  const row = "flex items-start gap-4 px-5 py-2";
  const icon = "mt-2.5 size-5 shrink-0 text-ink-3";

  return (
    <aside className="fixed inset-0 z-40 flex flex-col overflow-auto bg-surface md:static md:z-auto md:w-[400px] md:shrink-0 md:border-l md:border-rule">
      <div className="flex h-14 shrink-0 items-center gap-1 px-2">
        <button
          type="button"
          aria-label={t("common.close")}
          className="flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
          onClick={onClose}
        >
          <X className="size-5" />
        </button>
        <span className="flex-1" />
        {move ? (
          <>
            <button
              type="button"
              aria-label={t("tasks.moveUp")}
              title={t("tasks.moveUp")}
              disabled={!move.canUp}
              onClick={move.up}
              className="flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5 disabled:opacity-30"
            >
              <ArrowUp className="size-5" />
            </button>
            <button
              type="button"
              aria-label={t("tasks.moveDown")}
              title={t("tasks.moveDown")}
              disabled={!move.canDown}
              onClick={move.down}
              className="flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5 disabled:opacity-30"
            >
              <ArrowDown className="size-5" />
            </button>
          </>
        ) : null}
        <button
          type="button"
          aria-label={t("tasks.important")}
          aria-pressed={task.important}
          onClick={() => patch({ important: !task.important })}
          className="flex size-10 items-center justify-center rounded-full hover:bg-ink/5"
        >
          <Star
            className={`size-5 ${task.important ? "fill-[#f4b400] text-[#f4b400]" : "text-ink-2"}`}
          />
        </button>
        <button
          type="button"
          aria-label={t("tasks.delete")}
          title={t("tasks.delete")}
          disabled={remove.isPending}
          onClick={() => remove.mutate()}
          className="flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5 hover:text-danger"
        >
          <Trash2 className="size-5" />
        </button>
      </div>

      <div className="flex items-start gap-4 px-5 pt-1 pb-3">
        <span className="pt-2">
          <RoundCheck
            checked={task.completed}
            label={task.completed ? t("tasks.markOpen") : t("tasks.markDone")}
            onToggle={() => patch({ completed: !task.completed })}
          />
        </span>
        <textarea
          rows={1}
          aria-label={t("tasks.title")}
          className={`min-w-0 flex-1 resize-none border-b border-transparent bg-transparent py-1 text-[22px] leading-snug outline-none focus:border-accent [field-sizing:content] ${task.completed ? "text-ink-3 line-through" : "text-ink"}`}
          value={title}
          onChange={(e) => setTitle(e.target.value.replace(/\n/g, ""))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              notesRef.current?.focus();
            }
          }}
          onBlur={() => {
            if (title.trim() && title !== task.title) patch({ title: title.trim() });
          }}
        />
      </div>

      <div className={row}>
        <AlignLeft className={icon} />
        <textarea
          ref={notesRef}
          aria-label={t("tasks.notes")}
          placeholder={t("tasks.addDetails")}
          className="min-h-20 min-w-0 flex-1 resize-y rounded-lg bg-surface-2 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/40"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => {
            if (notes !== (task.notes ?? "")) patch({ notes: notes || null });
          }}
        />
      </div>

      <div className={row}>
        <CalendarDays className={icon} />
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <input
            type="date"
            aria-label={t("tasks.dueDate")}
            className={field}
            value={date}
            onChange={(e) =>
              patch(
                e.target.value
                  ? { due: `${e.target.value}T${time || "00:00"}`, dueHasTime: Boolean(time) }
                  : { due: null, dueHasTime: false },
              )
            }
          />
          <input
            type="time"
            aria-label={t("tasks.dueTime")}
            className={field}
            value={time}
            disabled={!date}
            onChange={(e) =>
              patch(
                e.target.value
                  ? { due: `${date}T${e.target.value}`, dueHasTime: true }
                  : { due: `${date}T00:00`, dueHasTime: false },
              )
            }
          />
          {date ? (
            <button
              type="button"
              aria-label={t("tasks.clearDue")}
              title={t("tasks.clearDue")}
              className="flex size-8 items-center justify-center rounded-full text-ink-3 hover:bg-ink/5"
              onClick={() => patch({ due: null, dueHasTime: false })}
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>
      </div>

      <div className={row}>
        <Bell className={icon} />
        <ReminderField task={task} onChange={(reminderMinutes) => patch({ reminderMinutes })} />
      </div>

      {lists.length > 1 ? (
        <div className={row}>
          <List className={icon} />
          <select
            aria-label={t("tasks.list")}
            className={`${field} min-w-0 flex-1`}
            value={task.listId}
            onChange={(e) => patch({ listId: e.target.value })}
          >
            {lists.map((list) => (
              <option key={list.id} value={list.id}>
                {listName(list, t)}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {task.email ? (
        <div className={row}>
          <Mail className={icon} />
          <Link
            to="/mail"
            search={{ email: task.email.id }}
            className="mt-2 min-w-0 flex-1 truncate text-sm text-accent hover:underline"
          >
            {t("tasks.openEmail", { subject: task.email.subject || t("tasks.fromEmail") })}
          </Link>
        </div>
      ) : null}

      <div className={row}>
        <ListChecks className={icon} />
        <div className="flex min-w-0 flex-1 flex-col">
          <ul className="flex flex-col">
            {subtasks.map((s) => (
              <li key={s.id} className="flex items-center gap-3 py-1.5">
                <RoundCheck
                  small
                  checked={s.completed}
                  label={s.completed ? t("tasks.markOpen") : t("tasks.markDone")}
                  onToggle={() => update.mutate({ id: s.id, patch: { completed: !s.completed } })}
                />
                <span
                  className={`min-w-0 flex-1 truncate text-sm ${s.completed ? "text-ink-3 line-through" : "text-ink"}`}
                >
                  {s.title}
                </span>
              </li>
            ))}
          </ul>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (subtask.trim()) addSubtask.mutate(subtask.trim());
            }}
          >
            <input
              className="mt-1 h-10 w-full rounded-lg bg-surface-2 px-3 text-sm outline-none focus:ring-2 focus:ring-accent/40"
              placeholder={t("tasks.addSubtask")}
              value={subtask}
              onChange={(e) => setSubtask(e.target.value)}
            />
          </form>
        </div>
      </div>
      <div className="px-5 py-3">
        <ErrorNotice error={update.error ?? addSubtask.error ?? remove.error} />
      </div>
    </aside>
  );
}

/** Reminder choice; the options depend on whether the due date has a time. */
function ReminderField({
  task,
  onChange,
}: {
  task: Task;
  onChange: (minutes: number | null) => void;
}) {
  const { t } = useTranslation();
  const options = task.dueHasTime ? TIMED_REMINDERS : DATE_REMINDERS;
  const current = task.due ? task.reminderMinutes : null;
  const custom = current !== null && !options.some((o) => o.minutes === current);
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <select
        aria-label={t("tasks.reminder")}
        className="h-10 rounded-lg bg-surface-2 px-3 text-sm outline-none focus:ring-2 focus:ring-accent/40 disabled:opacity-50"
        value={current === null ? "" : String(current)}
        disabled={!task.due}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      >
        <option value="">{t("tasks.reminderNone")}</option>
        {options.map((option) => (
          <option key={option.minutes} value={option.minutes}>
            {t(option.key)}
          </option>
        ))}
        {custom ? (
          <option value={current}>
            {current >= 0
              ? t("tasks.reminderCustomBefore", { minutes: current })
              : t("tasks.reminderCustomAfter", { minutes: -current })}
          </option>
        ) : null}
      </select>
      <p className="text-xs text-ink-3">
        {task.due ? (
          <>
            {t("tasks.reminderWhere")}{" "}
            <Link to="/settings/notifications" className="text-accent hover:underline">
              {t("tasks.notificationSettings")}
            </Link>
          </>
        ) : (
          t("tasks.reminderNeedsDue")
        )}
      </p>
    </div>
  );
}

/** The default list is stored as "Tasks"; show it in the reader's language. */
function listName(list: TaskList, t: (key: string) => string): string {
  return list.isDefault && list.name === "Tasks" ? t("nav.tasks") : list.name;
}

/**
 * "manual": the user's own order, with tasks never ordered here (made on a phone, say) after
 * them. "due": soonest due first, then important ones, then the rest.
 */
export function sortTasks(tasks: Task[], mode: SortMode = "due"): Task[] {
  const byDue = (a: Task, b: Task) => {
    if (a.due && b.due) return a.due.localeCompare(b.due);
    if (a.due) return -1;
    if (b.due) return 1;
    if (a.important !== b.important) return a.important ? -1 : 1;
    return a.title.localeCompare(b.title);
  };
  return [...tasks].sort((a, b) => {
    if (mode === "manual") {
      if (a.position !== null && b.position !== null) return a.position - b.position;
      if (a.position !== null) return -1;
      if (b.position !== null) return 1;
    }
    return byDue(a, b);
  });
}

function isOverdue(task: Task): boolean {
  if (!task.due) return false;
  const now = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  const today = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
  if (!task.dueHasTime) return task.due.slice(0, 10) < today;
  return new Date(task.due).getTime() < now.getTime();
}

function dueDay(task: Task): Date {
  const [y, m, d] = (task.due ?? "").slice(0, 10).split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Whole days from today to the due day; negative when it has passed. */
function relativeDays(task: Task, now = new Date()): number {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((dueDay(task).getTime() - today.getTime()) / 86_400_000);
}

/** "Today", "Tomorrow", "Yesterday" in the reader's language, a date otherwise; plus the time. */
export function dueLabel(task: Task, locale: string, now = new Date()): string {
  if (!task.due) return "";
  const days = relativeDays(task, now);
  let day: string;
  if (Math.abs(days) <= 1) {
    const word = new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(days, "day");
    day = word.charAt(0).toLocaleUpperCase(locale) + word.slice(1);
  } else {
    day = new Intl.DateTimeFormat(locale, {
      weekday: "short",
      day: "numeric",
      month: "short",
      ...(dueDay(task).getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
    }).format(dueDay(task));
  }
  return task.dueHasTime ? `${day}, ${task.due.slice(11, 16)}` : day;
}
